import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUi } from '@/store/ui-store';
import { Sidebar } from './Sidebar';

export interface MobileNavigationProps {
  attentionCount?: number;
}

/**
 * The sidebar as a slide-over below `lg`.
 *
 * It reuses `Sidebar` rather than duplicating the navigation, so adding a
 * section is a one-line change in `nav-items.ts`.
 */
export function MobileNavigation({ attentionCount }: MobileNavigationProps) {
  const { mobileNavOpen, closeMobileNav } = useUi();
  const location = useLocation();

  // A route change means the user navigated; the drawer has done its job.
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname, closeMobileNav]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMobileNav();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, closeMobileNav]);

  if (!mobileNavOpen) return null;

  return (
    <div className="lg:hidden">
      <div
        className="fixed inset-0 z-40 animate-kainos-fade-in bg-[rgba(15,23,42,0.35)]"
        onClick={closeMobileNav}
        aria-hidden="true"
      />
      <div className="fixed inset-y-0 left-0 z-50 animate-kainos-fade-in">
        <div className="relative h-full">
          <button
            type="button"
            onClick={closeMobileNav}
            aria-label="Cerrar navegación"
            className="absolute right-3 top-5 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-ink-faint hover:bg-line-subtle"
          >
            <X size={17} />
          </button>
          <Sidebar attentionCount={attentionCount} onNavigate={closeMobileNav} />
        </div>
      </div>
    </div>
  );
}
